import React, { useState, useEffect } from 'react';
import {
    createOption, deleteOption,
    getOptionEffects, createOptionEffect, deleteOptionEffect,
    getComponents, updateQuestion
} from '../../../api/autoEstimate';
import AdminButton from '../../../components/admin/AdminButton';

// Controlled panel: label edits are owned by the parent so a single row-level
// Save button can persist question fields + option labels in one request via
// the unified PUT /admin/questions/:id endpoint.
//
// Props:
//   question         : the question (with options) loaded from the server
//   versionId        : current version id (for components query)
//   optionLabels     : { [optionId]: string } — the parent's working draft
//   onLabelChange    : (optionId, newLabel) => void
//   onSaveRow        : () => Promise<void>  — invoked from inside-panel Enter
//   onOptionsMutated : () => Promise<void> — call after add/delete option so
//                      parent refreshes its draft baseline
const QuestionDetailPanel = ({
    question,
    versionId,
    optionLabels,
    onLabelChange,
    onSaveRow,
    onOptionsMutated,
}) => {
    const options = question?.options || [];

    // Pricing Data (Effects)
    const [effectsMap, setEffectsMap] = useState({}); // { optionId: [effects] }
    const [allComponents, setAllComponents] = useState([]);

    // Inline Pricing Form State
    const [addEffectTargetId, setAddEffectTargetId] = useState(null); // optionId where form is open
    const [searchTerm, setSearchTerm] = useState('');
    const [addEffectQuantity, setAddEffectQuantity] = useState(1);

    useEffect(() => {
        if (!question?.id) return;
        loadComponents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question?.id]);

    useEffect(() => {
        options.forEach(o => {
            if (!(o.id in effectsMap)) loadEffects(o.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question?.options]);

    const loadComponents = async () => {
        try {
            const comps = await getComponents(versionId);
            setAllComponents(comps);
        } catch (e) {
            console.error("Failed to load components", e);
        }
    };

    const loadEffects = async (optId) => {
        try {
            const data = await getOptionEffects(optId);
            setEffectsMap(prev => ({ ...prev, [optId]: data }));
        } catch (e) {
            console.error(e);
        }
    };

    // --- Question Type Change ---
    // Type change is destructive (drops every existing option), so we leave it
    // outside the row's unified-save flow: it's an immediate, confirmed action.
    const handleTypeChange = async (newType) => {
        if (newType === question.type) return;

        if (options.length > 0) {
            if (!window.confirm('질문 유형을 변경하면 기존 옵션이 모두 삭제됩니다. 계속하시겠습니까?')) {
                return;
            }
            try {
                await Promise.all(options.map(o => deleteOption(o.id)));
            } catch (e) {
                alert('기존 옵션 삭제 중 오류가 발생했습니다.');
                return;
            }
        }

        try {
            await updateQuestion(question.id, { type: newType });
            if (onOptionsMutated) await onOptionsMutated();
        } catch (e) {
            alert('질문 유형 변경 실패');
        }
    };

    // --- Option CRUD ---

    const handleAddOption = async () => {
        if ((question.type === 'NUMBER' || question.type === 'TEXT') && options.length >= 1) {
            return alert('숫자/텍스트 입력 질문은 하나의 옵션만 가질 수 있습니다. (라벨/단가 설정용)');
        }

        try {
            await createOption(question.id, {
                questionId: question.id,
                label: (question.type === 'NUMBER' || question.type === 'TEXT') ? '라벨/항목명' : '새 옵션',
                sortOrder: options.length + 1
            });
            if (onOptionsMutated) await onOptionsMutated();
        } catch (e) {
            alert('옵션 추가 실패');
        }
    };

    const handleDeleteOption = async (id) => {
        if (!window.confirm('옵션을 삭제하시겠습니까?')) return;
        try {
            await deleteOption(id);
            if (onOptionsMutated) await onOptionsMutated();
            setEffectsMap(prev => { const n = { ...prev }; delete n[id]; return n; });
        } catch (e) {
            alert('옵션 삭제 실패');
        }
    };

    const handleLabelKeyDown = (e) => {
        if (e.key === 'Enter' && onSaveRow) {
            e.preventDefault();
            onSaveRow();
        }
    };

    // --- Pricing Logic ---

    const openEffectForm = (optId) => {
        setAddEffectTargetId(optId);
        setSearchTerm('');
        setAddEffectQuantity(question.type === 'NUMBER' ? 0 : 1);
    };

    const closeEffectForm = () => {
        setAddEffectTargetId(null);
    };

    const handleAddEffectByCode = async (optId, code) => {
        try {
            await createOptionEffect(optId, {
                componentCode: code,
                effectJson: { amount: null, quantity: addEffectQuantity },
                versionId: parseInt(versionId)
            });
            await loadEffects(optId);
            setSearchTerm('');
        } catch (e) {
            alert('가격 요소 추가 실패');
        }
    };

    const handleDeleteEffect = async (optId, effId) => {
        if (!window.confirm('가격 변동을 삭제하시겠습니까?')) return;
        try {
            await deleteOptionEffect(effId);
            loadEffects(optId);
        } catch (e) {
            alert('삭제 실패');
        }
    };

    return (
        <div style={{ padding: '20px', background: '#fafafa', borderLeft: '4px solid #0056b3' }}>
            {/* Header: Type Selector */}
            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>질문 유형:</span>
                <div className="btn-group" style={{ display: 'flex', gap: '5px' }}>
                    {['RADIO', 'CHECKBOX', 'SELECT', 'NUMBER', 'TEXT'].map(type => (
                        <button
                            key={type}
                            onClick={() => handleTypeChange(type)}
                            style={{
                                padding: '4px 10px',
                                fontSize: '0.8rem',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                background: question.type === type ? '#007bff' : 'white',
                                color: question.type === type ? 'white' : '#333',
                                cursor: 'pointer',
                                fontWeight: question.type === type ? 'bold' : 'normal'
                            }}
                        >
                            {type}
                        </button>
                    ))}
                </div>
                <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: 'auto' }}>
                    옵션 라벨 변경은 행의 [저장] 버튼으로 함께 저장됩니다
                </span>
            </div>

            <table className="admin-table" style={{ fontSize: '0.9rem', width: '100%', background: 'white' }}>
                <thead>
                    <tr style={{ background: '#f1f3f5', textAlign: 'center' }}>
                        <th style={{ width: '50px' }}>No.</th>
                        <th style={{ width: '250px' }}>옵션명 (Label)</th>
                        <th>코드(명칭:가격)</th>
                        <th style={{ width: '80px' }}>삭제</th>
                    </tr>
                </thead>
                <tbody>
                    {options.length === 0 && (
                        <tr>
                            <td colSpan="4" style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                                등록된 옵션이 없습니다.
                            </td>
                        </tr>
                    )}
                    {options.map((opt, idx) => {
                        const effects = effectsMap[opt.id] || [];
                        const draft = optionLabels?.[opt.id];
                        const value = draft !== undefined ? draft : opt.label;
                        const isDirty = draft !== undefined && draft !== opt.label;

                        return (
                            <tr key={opt.id}>
                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                <td>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={value}
                                        onChange={(e) => onLabelChange && onLabelChange(opt.id, e.target.value)}
                                        onKeyDown={handleLabelKeyDown}
                                        style={{
                                            width: '100%',
                                            borderColor: isDirty ? '#ffc107' : '#ced4da',
                                            background: isDirty ? '#fff8e1' : 'white'
                                        }}
                                        placeholder="옵션명 입력"
                                    />
                                </td>
                                <td>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
                                        {effects.map(eff => {
                                            const comp = allComponents.find(c => c.code === eff.componentCode);
                                            const qty = eff.effectJson?.quantity || 1;
                                            const label = comp
                                                ? `${comp.code} (${comp.name} : ${comp.defaultPrice?.toLocaleString()}원 : ${qty})`
                                                : `${eff.componentCode} (Qty: ${qty})`;
                                            return (
                                                <span key={eff.id} style={{
                                                    background: '#e3f2fd', padding: '4px 10px', borderRadius: '16px', fontSize: '0.85rem',
                                                    display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #90caf9', color: '#0d47a1'
                                                }}>
                                                    <span>{label}</span>
                                                    <button
                                                        onClick={() => handleDeleteEffect(opt.id, eff.id)}
                                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#d32f2f', fontWeight: 'bold', fontSize: '1.1em', lineHeight: 1, padding: 0 }}
                                                    >
                                                        &times;
                                                    </button>
                                                </span>
                                            );
                                        })}

                                        <button
                                            className="admin-btn-secondary"
                                            style={{ padding: '0px 6px', borderRadius: '50%', minWidth: '24px', height: '24px', lineHeight: '22px', fontSize: '14px' }}
                                            onClick={() => openEffectForm(opt.id)}
                                        >
                                            +
                                        </button>
                                    </div>
                                </td>
                                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    <AdminButton variant="danger" size="sm" onClick={() => handleDeleteOption(opt.id)}>삭제</AdminButton>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <AdminButton
                variant="secondary"
                className="!bg-blue-50 !text-blue-600 border-dashed w-full mt-2"
                onClick={handleAddOption}
            >
                + 옵션 추가
            </AdminButton>

            {/* Component Selection Modal */}
            {addEffectTargetId && (
                <div className="editor-overlay" style={{ zIndex: 1200 }}>
                    <div className="admin-form" style={{
                        width: '450px', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
                        background: 'white', padding: '20px', borderRadius: '8px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
                    }}>
                        <h3 style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                            가격 요소 추가
                        </h3>

                        <div style={{ background: '#e8f5e9', padding: '10px', borderRadius: '4px', marginBottom: '15px', fontSize: '0.85rem', color: '#2e7d32' }}>
                            <strong>🧮 계산 방식 ({question.type})</strong>
                            <div style={{ marginTop: '4px' }}>
                                {question.type === 'NUMBER'
                                    ? <span>최종 수량 = <b>사용자 입력값</b> + <b style={{ color: '#c62828' }}>설정 수량(Offset)</b></span>
                                    : <span>최종 가격 = (단가 × <b style={{ color: '#c62828' }}>설정 수량(Multiplier)</b>)</span>
                                }
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                            <div style={{ width: '80px' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '5px' }}>수량</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    style={{ textAlign: 'center' }}
                                    value={addEffectQuantity}
                                    onChange={(e) => setAddEffectQuantity(parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '5px' }}>검색</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="코드 또는 명칭으로 검색..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', background: 'white' }}>
                            {allComponents
                                .filter(c => c.code.toLowerCase().includes(searchTerm.toLowerCase()) || c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                .filter(c => !(effectsMap[addEffectTargetId] || []).find(e => e.componentCode === c.code))
                                .map(c => (
                                    <div
                                        key={c.id}
                                        style={{
                                            padding: '12px 15px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #eee',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            background: 'white'
                                        }}
                                        onClick={() => handleAddEffectByCode(addEffectTargetId, c.code)}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f1f3f5'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                    >
                                        <div>
                                            <b style={{ color: '#0d47a1' }}>{c.code}</b>
                                            <span style={{ marginLeft: '8px', color: '#666' }}>{c.name}</span>
                                        </div>
                                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>{c.defaultPrice?.toLocaleString()}원</span>
                                    </div>
                                ))
                            }
                            {allComponents.filter(c => c.code.toLowerCase().includes(searchTerm.toLowerCase()) || c.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                                <div style={{ padding: '20px', color: '#999', textAlign: 'center' }}>검색 결과 없음</div>
                            )}
                        </div>
                        <AdminButton
                            variant="secondary"
                            fullWidth
                            className="mt-4"
                            onClick={closeEffectForm}
                        >
                            닫기
                        </AdminButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionDetailPanel;
